import type { RgbaFrame } from './types';

function copyFrame(frame: RgbaFrame): RgbaFrame {
  return {
    width: frame.width,
    height: frame.height,
    data: new Uint8Array(frame.data),
  };
}

function framesPixelEqual(a: RgbaFrame, b: RgbaFrame): boolean {
  if (a.width !== b.width || a.height !== b.height) return false;
  const da = a.data;
  const db = b.data;
  if (da.length !== db.length) return false;
  for (let i = 0; i < da.length; i++) {
    if (da[i] !== db[i]) return false;
  }
  return true;
}

/**
 * 视觉不可辨、像素不完全相同的拷贝。
 * 部分编码器会把完全相同的相邻帧合并成静图。
 */
export function nearlyIdenticalFrame(frame: RgbaFrame): RgbaFrame {
  const out = copyFrame(frame);
  const { width, height, data } = out;
  if (width < 1 || height < 1) return out;

  const x = width - 1;
  const y = height - 1;
  const i = (y * width + x) * 4;
  const a = data[i + 3]!;
  if (a >= 1) {
    let a2 = a > 0 ? a - 1 : 0;
    if (a2 === a) a2 = Math.min(255, a + 1);
    data[i + 3] = a2;
  } else {
    data[i] = data[i]! ^ 1;
  }
  return out;
}

/** 伪静态：双内容帧 + 微差 */
export function buildPseudoStaticFrames(content: RgbaFrame): RgbaFrame[] {
  return [copyFrame(content), nearlyIdenticalFrame(content)];
}

/** 封面 + 内容；尺寸已对齐时直接使用 */
export function buildCoverRevealFrames(
  cover: RgbaFrame,
  content: RgbaFrame,
): RgbaFrame[] {
  if (cover.width !== content.width || cover.height !== content.height) {
    throw new Error('封面与内容尺寸未对齐');
  }
  const f0 = copyFrame(cover);
  let f1 = copyFrame(content);
  if (framesPixelEqual(f0, f1)) {
    f1 = nearlyIdenticalFrame(f1);
  }
  return [f0, f1];
}
